import { datadogRum } from '@datadog/browser-rum'

// RUM records failed requests as resources, so this wrapper reports failures to Error Tracking.

type Config = {
  maxBodyChars: number
  captureBody: boolean
  ignoredOrigins: string[]
  redactKeys: string[]
}

const DEFAULTS: Config = {
  maxBodyChars: 2000,
  captureBody: true,
  ignoredOrigins: [
    'launchdarkly.com', // feature flags
    'segment.io', // analytics
    'segment.com',
    'usepylon.com', // support widget
    'getbeamer.com', // product updates
    'courier.com', // notifications
    'flagcdn.com', // flag assets
  ],
  redactKeys: [
    'token', // access_token, refresh_token, id_token, courierToken, oneSchemaToken, etc.
    'auth', // authorization header
    'password',
    'secret', // client_secret, api_secret, AUTH_SECRET, etc.
    'api_key',
    'credential',
    'session', // session cookies/identifiers
    'cookie', // cookie + set-cookie headers
    'fingerprint', // omnea_fingerprint_* security cookies
  ],
}

const escapeRegExp = (str: string): string => String(str).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

const truncate = (s: string, max: number): string => {
  if (s.length <= max) return s
  return s.slice(0, max) + '…'
}

const stripSeparators = (value: string): string => value.replace(/[-_]/g, '')

const shouldRedactKey = (k: string, redactKeys: string[]): boolean => {
  const key = stripSeparators(String(k).toLowerCase())
  return redactKeys.some((redactKey) => key.includes(stripSeparators(redactKey)))
}

const redactSecrets = (text: string, redactKeys: string[]): string => {
  if (!text) return text

  let out = text

  for (const k of redactKeys) {
    const key = k.split(/[-_]/).map(escapeRegExp).join('[-_]?')
    const jsonRe = new RegExp(`("[^"]*${key}[^"]*"\\s*:\\s*)"(.*?)"`, 'gi')
    out = out.replace(jsonRe, '$1"[REDACTED]"')

    const kvRe = new RegExp(`(${key}\\s*[=:]\\s*)([^\\s&",}]+)`, 'gi')
    out = out.replace(kvRe, '$1[REDACTED]')
  }

  out = out.replace(/(Authorization\s*[:=]\s*Bearer\s+)([A-Za-z0-9._-]+)/gi, '$1[REDACTED]')
  out = out.replace(/(Bearer\s+)([A-Za-z0-9._-]+)/gi, '$1[REDACTED]')

  return out
}

const redactUrlSecrets = (url: string, redactKeys: string[]): string => {
  try {
    const u = new URL(url, window.location.href)
    const keys = Array.from(u.searchParams.keys())
    for (const key of keys) {
      if (shouldRedactKey(key, redactKeys)) {
        u.searchParams.set(key, '[REDACTED]')
      } else if (key === 'ddforward') {
        // Datadog's proxy nests its telemetry intake URL's path and query in this parameter.
        const forwarded = new URL(u.searchParams.get(key) ?? '', u.origin)
        for (const forwardedKey of forwarded.searchParams.keys()) {
          if (shouldRedactKey(forwardedKey, redactKeys)) {
            forwarded.searchParams.set(forwardedKey, '[REDACTED]')
          }
        }
        u.searchParams.set(key, `${forwarded.pathname}${forwarded.search}${forwarded.hash}`)
      }
    }
    return u.toString()
  } catch {
    return redactSecrets(String(url), redactKeys)
  }
}

const isIgnoredOrigin = (url: string, ignoredOrigins: string[]): boolean => {
  try {
    const hostname = new URL(url, window.location.href).hostname
    return ignoredOrigins.some((origin) => hostname === origin || hostname.endsWith(`.${origin}`))
  } catch {
    return false
  }
}

// Datadog intake requests carry these query parameters, either directly or inside a proxy's ddforward value.
const DATADOG_INTAKE_URL_PARAMETERS = ['ddsource', 'dd-api-key', 'dd-request-id']

const isDatadogIntakeUrl = (url: string): boolean => {
  try {
    const parsed = new URL(url, window.location.href)
    const hasIntakeParameters = (params: URLSearchParams) =>
      DATADOG_INTAKE_URL_PARAMETERS.every((parameter) => params.has(parameter))

    if (hasIntakeParameters(parsed.searchParams)) return true

    const forwarded = parsed.searchParams.get('ddforward')
    return forwarded !== null && hasIntakeParameters(new URL(forwarded, parsed.origin).searchParams)
  } catch {
    return false
  }
}

const normalizeRequest = (input: RequestInfo | URL, init?: RequestInit): { url: string; method: string } => {
  const method = (init?.method ?? (input instanceof Request ? input.method : null) ?? 'GET').toUpperCase()

  const url = typeof input === 'string' ? input : input instanceof Request ? input.url : String(input)

  return { url, method }
}

type ErrorContext = {
  kind: string
  url: string
  method: string
  status: number
  statusText: string
  durationMs: number
  requestBody?: string
  requestBodyTruncated?: boolean
  responseBody?: string
  responseBodyTruncated?: boolean
  responseContentType?: string
}

const extractRequestBody = (input: RequestInfo | URL, init?: RequestInit): BodyInit | null | undefined =>
  init?.body ?? (input instanceof Request ? input.body : null)

const safeReadRequestBody = (
  cfg: Config,
  input: RequestInfo | URL,
  init?: RequestInit
): { requestBody?: string; requestBodyTruncated?: boolean } => {
  try {
    const body = extractRequestBody(input, init)
    if (body == null) return {}

    const text = typeof body === 'string' ? body : body instanceof URLSearchParams ? body.toString() : null
    if (text == null) return {}

    const redacted = redactSecrets(text, cfg.redactKeys)
    return {
      requestBody: truncate(redacted, cfg.maxBodyChars),
      requestBodyTruncated: redacted.length > cfg.maxBodyChars,
    }
  } catch {
    return {}
  }
}

const isTextContentType = (ct: string): boolean =>
  ct.includes('application/json') ||
  ct.includes('text/') ||
  ct.includes('application/problem+json') ||
  ct.includes('application/xml') ||
  ct.includes('application/x-www-form-urlencoded')

const buildSafeErrorContext = async (
  cfg: Config,
  {
    url,
    method,
    status,
    statusText,
    durationMs,
    response,
    input,
    init,
  }: {
    url: string
    method: string
    status: number
    statusText: string
    durationMs: number
    response: Response | null
    input: RequestInfo | URL
    init?: RequestInit
  }
): Promise<ErrorContext> => {
  const safeUrl = redactUrlSecrets(url, cfg.redactKeys)

  const context: ErrorContext = {
    kind: 'http_error',
    url: safeUrl,
    method,
    status,
    statusText,
    durationMs,
    ...(isDatadogIntakeUrl(url) ? {} : safeReadRequestBody(cfg, input, init)),
  }

  // Datadog intake payloads contain serialized RUM events, including user data.
  if (!cfg.captureBody || !response) return context

  try {
    const ct = response.headers?.get?.('content-type') ?? ''
    if (!isTextContentType(ct)) return context

    const text = await response.text()
    const redacted = redactSecrets(text, cfg.redactKeys)

    context.responseBody = truncate(redacted, cfg.maxBodyChars)
    context.responseBodyTruncated = redacted.length > cfg.maxBodyChars
    context.responseContentType = ct
  } catch {
    // Swallow body-read/CORS errors; keep context minimal
  }

  return context
}

let installed = false

export const installDDFetchErrorCapture = (options: Partial<Config> = {}) => {
  if (typeof window === 'undefined') return
  if (installed) return
  installed = true

  const cfg: Config = { ...DEFAULTS, ...options }
  const originalFetch = window.fetch.bind(window)

  window.fetch = async function safeFetch(input, init) {
    const startedAt = Date.now()
    const { url, method } = normalizeRequest(input, init)

    const ignored = isIgnoredOrigin(url, cfg.ignoredOrigins)

    try {
      const res = await originalFetch(input, init)

      if (!res.ok && !ignored) {
        const resClone = isDatadogIntakeUrl(url) ? null : res.clone()
        void (async () => {
          try {
            const context = await buildSafeErrorContext(cfg, {
              url,
              method,
              status: res.status,
              statusText: res.statusText,
              durationMs: Date.now() - startedAt,
              response: resClone,
              input,
              init,
            })

            datadogRum.addError(new Error(`HTTP ${res.status} ${method} ${context.url}`), context)
          } catch {
            // Swallow errors from observability to avoid impacting callers
          }
        })()
      }

      return res
    } catch (err: unknown) {
      const isAbortError = err instanceof DOMException && err.name === 'AbortError'
      // Keep non-2xx Datadog intake responses as the signal that a proxy blocked telemetry.
      if (!ignored && !isDatadogIntakeUrl(url) && !isAbortError) {
        const error = err instanceof Error ? err : new Error(String(err))
        const context = {
          kind: 'network_error',
          url: redactUrlSecrets(url, cfg.redactKeys),
          method,
          durationMs: Date.now() - startedAt,
          ...safeReadRequestBody(cfg, input, init),
        }

        datadogRum.addError(error, context)
      }

      throw err
    }
  }
}
